import React, { useEffect, useMemo, useRef, useState } from 'react';
import mobileGameDefaultIcon from '@/pages/opinion/assets/svg/mobile-game-default.svg';

import { echartCountMat, getPercentageInfo } from '@/pages/opinion/utils';
import EChartCommon from '@/pages/opinion/components/EChartCommon';
import { debounce } from 'lodash-es';
import { avgValueFormat, reviewsFormat } from '@/pages/opinion/utils/value';
import {
  echartTooltipTime,
  formatChartParamsDate,
} from '@/pages/opinion/components/ChartCommon/formatter';
import { useIntl } from 'umi';
import { getIconInfo } from './utils';
import { chartAllColors } from '@/pages/opinion/constants';
import { xArr } from '../../hooks/useGameChartRich';

interface BarStackChartsProp {
  // 后台返回的原数据 ， 为空时不要传入进来
  data: any[];
  // 自定义高 默认300
  height?: number;
  /**
   * tab 组件内需要监听
   *
   * - 设置为 `mentions`，则会提供B、M、K的数值单位
   * - 设置为 `mentions_dau`，则会提供万分符的B、M、K数值单位
   *
   */
  mentionTab: 'mentions' | 'mentions_dau';
  // 第二个tab ，没有第二个可以不传
  otherTab?: any;
  // 右边下拉的value
  selectValue?: any;
  // 色块的点击事件，有一个参数返回
  clickChart?: (v: any) => void;
  // 游戏信息 包括name cover id ， 为空时不要传入进来
  xAxisList: any[];
  loading: boolean;
  // 筛选器参数
  commonParams: any;
  transformFunc: (value: string) => string;
  onClick: (value: string) => void;
  title?: string;
}

export default function BarStackCharts({
  data,
  height = 300,
  xAxisList,
  commonParams,
  mentionTab,
  loading,
  title,
  transformFunc = (value: string) => value,
  onClick = (value: string) => {},
}: BarStackChartsProp) {
  const { data: realData, xAxisData } = useMemo(() => {
    if (!data || data.length === 0) return {};
    const timeText = echartTooltipTime(commonParams);
    // 第一层
    const newData = data?.map?.((item: any, index: number) => {
      // 求和
      const gameTotal = item.games?.reduce(
        (pre: number, cur: { value: number }) => {
          return cur.value + pre;
        },
        0,
      );
      // 第二层
      const gameData = item.games?.map((val: any, i: number) => {
        const gameIndex = xAxisList?.findIndex(
          (v: any) => v.edition_unified_id === val.edition_unified_id,
        );
        return {
          name: xAxisList?.[gameIndex]?.entity_name, // 等数据
          cover: xAxisList?.[gameIndex]?.cover || mobileGameDefaultIcon, // 等数据
          value: val.value,
          rate: val.rate,
          class: xArr[i],
          time: timeText,
          stackName: item.name,
          total: index === data?.length - 1 ? gameTotal : '',
        };
      });
      return {
        type: 'bar',
        stack: 'overviewBar',
        name: item.name,
        data: gameData,
        barMaxWidth: '70px',
      };
    });

    const newLegend = data?.[0]?.games?.map((v) => v.name);
    return { data: newData, xAxisData: newLegend };
  }, [data, xAxisList, commonParams]);
  const { formatMessage } = useIntl();
  const [chartData, setChartData] = useState<Array<any>>([]);

  useEffect(() => {
    setChartData(realData || []);
  }, [realData]);

  const activeRef = useRef('');
  const chartOver = debounce((param: any) => {
    const { seriesIndex, seriesName } = param;
    if (activeRef.current === seriesName) {
      return;
    }
    activeRef.current = seriesName;
    const newData = JSON.parse(JSON.stringify(realData));
    newData.forEach((item: any, index: number) => {
      item.data.forEach((v: any, i: number) => {
        if (index === seriesIndex && v.value > 0) {
          newData[index].data[i].itemStyle = {
            opacity: 1,
            borderColor: '#000',
            borderWidth: 2,
          };
        } else {
          newData[index].data[i].itemStyle = {
            opacity: 0.1,
          };
          newData[index].emphasis = {};
        }
      });
    });
    setChartData(newData);
  }, 100);
  const formatValue = (value: number) => {
    let multiple = 1,
      func = reviewsFormat,
      suffix = '';
    if (mentionTab === 'mentions_dau') {
      multiple = 10000;
      func = avgValueFormat;
      suffix = '‱';
    }
    const data = func(value * multiple);
    if (data === '-') return '-';
    return data + suffix;
  };

  const [legendName, setLegendName] = useState();
  const legendClick = (param: any) => {
    setLegendName(param.selected);
  };

  const options = useMemo(() => {
    const xRich: any = {};
    // 处理x轴图片显示
    chartData?.[0]?.data?.forEach((v: any) => {
      xRich[v.class] = {
        backgroundColor: {
          image: v.cover,
        },
        align: 'center',
        width: 25,
        height: 25,
      };
    });

    const realLegend = chartData?.map((v: any, i: number) => ({
      name: v.name,
      icon: 'circle',
      itemStyle: {
        color: chartAllColors[i],
      },
    }));

    const option = {
      tooltip: {
        trigger: 'item',
        axisPointer: {
          type: 'shadow',
        },
        confine: true,
        hideDelay: 200,
        triggerOn: 'mousemove',
        formatter: (param: any) => {
          // 选择整个柱子时展示这个
          if (Array.isArray(param)) {
            let { data } = param[0];
            let { cover, name } = data;
            const params = [...param]
              .filter((a) => a.value !== 0)
              .sort((a, b) => b.value - a.value);
            if (params.length === 0) {
              return '';
            }
            return formatChartParamsDate({
              params: params,
              listTitleFunc: (item) => transformFunc(item.seriesName),
              valueFormat: (value) => formatValue(value),
              dealwithRecords: (records: Array<any>, total: number) => {
                return records.map((item) => {
                  return {
                    ...item,
                    share: getPercentageInfo(item.originValue, total, 2),
                  };
                });
              },
              tablesConfigFunc: (records: Array<any>) => [
                {
                  showHeader: true,
                  records,
                  columns: [
                    {
                      key: 'text',
                      header: getIconInfo(cover, name),
                    },
                    {
                      key: 'value',
                      align: 'right',
                      weight: 'bold',
                      header: formatMessage({
                        id: title,
                      }),
                    },
                    {
                      key: 'share',
                      align: 'right',
                      weight: 'bold',
                      header: formatMessage({ id: 'Share-1' }),
                    },
                  ],
                },
              ],
            });
          } else {
            // 选中某个块的时候展示这个
            const { seriesIndex, seriesName } = param;
            let { data } = chartData[seriesIndex];
            const params = [...data]
              .filter((a) => a.value !== 0)
              .sort((a, b) => b.value - a.value);
            if (params.length === 0) {
              return '';
            }
            return formatChartParamsDate({
              params: params,
              textShowFun: (
                marker: string,
                seriesName: string,
                element: any,
              ) => {
                return getIconInfo(element.cover, element.name);
              },
              valueFormat: (value) => formatValue(value),
              dealwithRecords: (records: Array<any>, total: number) => {
                return records.map((item) => {
                  return {
                    ...item,
                    share: getPercentageInfo(item.originValue, total, 2),
                  };
                });
              },
              tablesConfigFunc: (records: Array<any>) => [
                {
                  showHeader: true,
                  records,
                  columns: [
                    {
                      key: 'text',
                      header: transformFunc(seriesName),
                    },
                    {
                      key: 'value',
                      align: 'right',
                      weight: 'bold',
                      header: formatMessage({
                        id: title,
                      }),
                    },
                    {
                      key: 'share',
                      align: 'right',
                      weight: 'bold',
                      header: formatMessage({ id: 'Share-1' }),
                    },
                  ],
                },
              ],
            });
          }
        },
      },
      grid: {
        top: '4%',
        right: 0,
        containLabel: true,
      },
      legend: {
        data: realLegend,
        selected:
          legendName || realLegend.map((item) => ({ [item.name]: true })),
        formatter: (value: string) => {
          return transformFunc(value);
        },
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
        axisLabel: {
          formatter: function (value: any, index: number) {
            return '{' + xArr[index] + '| }';
          },
          rich: {
            value: {
              lineHeight: 30,
              align: 'center',
              width: 300,
              height: 300,
            },
            ...xRich,
          },
        },
        axisPointer: {
          show: true,
          type: 'shadow',
          label: {
            show: false,
          },
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: any) => {
            return mentionTab === 'mentions_dau'
              ? echartCountMat(value * 10000) + '‱'
              : echartCountMat(value);
          },
        },
      },
      series: chartData,
    };
    return option;
  }, [chartData, xAxisData, formatMessage, mentionTab, title]);

  const eventsDict = useMemo(() => {
    return {
      mouseover: chartOver,
      mouseout: debounce(() => {
        activeRef.current = '';
        setChartData(realData || []);
      }, 100),
      click: (param: any) => {
        onClick?.(param.seriesName);
      },
      globalout: () => {
        activeRef.current = '';
        setChartData(realData || []);
      },
      legendselectchanged: legendClick,
    };
  }, [realData]);

  return (
    <EChartCommon
      empty={!realData || realData.length === 0}
      loading={loading}
      height={height}
      options={options}
      eventsDict={eventsDict}
      optionsProps={{ excludeOptions: ['axisPointer'] }}
    ></EChartCommon>
  );
}
